import { get } from 'data/services/apiService';
import { API_URL } from 'data/services/apiConstants';
import { dataConfig } from 'data/dataConfig';
import {
	getReducerName,
	getDataSourceApiUrlPath,
	CITYPAY,
	NYC_GOV,
	COMBINED,
	FDNY_SCRAPER,
	COUNTY_CLERK,
} from 'data/dataSourceConstants';
import { sortToString } from 'utils/sort';
import { updatePropertyCount } from './propertyReducer';

const ITEMS_PER_PAGE = 20;

export const getInitialState = () => ({
	isLoading: false,
	propertyId: null,
	filteredStatus: null,
	searchString: '',

	sortField: null,
	sortDirection: null,

	data: [],

	totalItems: null,
	numOpen: null,
	numClosed: null,

	currentPage: null,
	totalPages: null,
	itemsPerPage: null,

	dataSetUrl: null,
	dataSetLastModified: null,
	statuses: [],
	closedStatuses: [],
	// dataset_last_modified

	// Copy the api wants shown instead of the generic empty-table text when a
	// dataset does not apply to this property at all (e.g. DOT Liens outside
	// Manhattan). null everywhere the api does not send empty_state_message.
	emptyStateMessage: null,
});

export const buildQueryParams = (params = {}) => {
	let queryParams = {
		// sort: params.sort || 'id|desc',
		page: params.page || 1,
		per_page: params.perPage ?? ITEMS_PER_PAGE,
	};

	if (params.status) {
		queryParams.status = params.status;
	}

	if (params.sortString) {
		queryParams.sort = params.sortString;
	}

	if (params.filter && params.filter.length) {
		queryParams.filter = params.filter;
	} else if (params.filter === '') {
		delete queryParams.filter;
	}

	return new URLSearchParams(queryParams).toString();
};

export function buildPropertyDatasetListUrl(
	propertyId,
	configObj,
	dataSource,
	queryString,
) {
	const base = `${API_URL}/api/property/${propertyId}`;
	if (
		dataSource === CITYPAY ||
		dataSource === NYC_GOV ||
		dataSource === COMBINED ||
		dataSource === FDNY_SCRAPER ||
		dataSource === COUNTY_CLERK
	) {
		return `${base}/${configObj.urlPath}?${queryString}`;
	}
	const pathSeg = getDataSourceApiUrlPath(dataSource);
	return `${base}/${pathSeg}/${configObj.urlPath}?${queryString}`;
}

/**
 * Fetches only num_open from the dataset list API and updates propertyData (sidebar / badges).
 * Used when PropertyDatasetUpdated fires so counts stay in sync without opening the table.
 */
export const fetchOpenIssueCountOnly =
	(propertyId, dataSetName, dataSource) => (dispatch) => {
		const configObj = dataConfig[dataSetName];
		if (!configObj) {
			return Promise.resolve();
		}
		const countField = configObj.openIssueCountFields?.[dataSource];
		if (!countField) {
			return Promise.resolve();
		}

		const status = configObj.hasStatus === false ? null : 'open';
		const params = buildQueryParams({
			status,
			page: 1,
			perPage: 1,
			filter: '',
			sortString: '',
		});
		const url = buildPropertyDatasetListUrl(
			propertyId,
			configObj,
			dataSource,
			params,
		);

		return get({
			dispatch,
			url,
			onSuccess: (results) => {
				if (results.num_open !== null && results.num_open !== undefined) {
					dispatch(updatePropertyCount(countField, results.num_open));
				}
			},
			onError: () => {},
		});
	};

export const apiRequest = (
	actionType,
	propertyId,
	status,
	searchString,
	sort,
) => ({
	type: actionType,
	payload: {
		isLoading: true,
		filteredStatus: status,
		searchString,
		propertyId,
		sortField: sort.field || null,
		sortDirection: sort.direction || null,
	},
});

export const apiSuccess = (actionType, results) => ({
	type: actionType,
	payload: {
		isLoading: false,
		data: results?.data || [],

		totalItems: results.total,
		numOpen: results.num_open,
		numClosed: results.num_closed,
		numCombined: results?.num_combined || 0,

		currentPage: results.current_page,
		totalPages: results.last_page,
		itemsPerPage: parseInt(results.per_page),
		dataSetUrl: results.dataset_url,
		dataSetLastModified: results.dataset_last_modified || null,
		statuses: results.statuses || [],
		closedStatuses: results.closed_statuses || [],
		emptyStateMessage: results.empty_state_message || null,
	},
});

/**
 * Whether a list response's num_open describes the whole property, and so may
 * stand in for the sidebar badge.
 *
 * The server computes num_open over whatever subset the request asked for. A
 * search filter, a page other than the first, or a status other than open all
 * narrow that subset, and writing a narrower figure into the badge is what makes
 * the number change once the dataset table loads.
 */
export const isPropertyWideCount = (status, searchString, page) => {
	const isOpenScoped = status === 'open' || status === null;
	const isUnfiltered = !searchString;
	const isFirstPage = page === null || page === undefined || Number(page) === 1;

	return isOpenScoped && isUnfiltered && isFirstPage;
};

export const apiError = (actionType, results) => ({
	type: actionType,
	payload: {
		isLoading: false,
		data: [],
	},
});

export default function buildGenericDataSetReducer(dataConfig, dataSource) {
	const API_REQUEST = `${getReducerName(dataConfig, dataSource)}_REQUEST`;
	const API_SUCCESS = `${getReducerName(dataConfig, dataSource)}_SUCCESS`;
	const API_ERROR = `${getReducerName(dataConfig, dataSource)}_ERROR`;

	const initialState = getInitialState();

	const setPage = (pageNumber) => (dispatch, getState) => {
		let state = getState()[getReducerName(dataConfig, dataSource)];
		if (pageNumber >= 1 && pageNumber <= state.totalPages) {
			return dispatch(
				fetchData(
					state.propertyId,
					state.filteredStatus,
					pageNumber,
					state.searchString,
					{ field: state.sortField, direction: state.sortDirection },
				),
			);
		}
	};

	const fetchData =
		(
			propertyId,
			status = null,
			page = 1,
			searchString = '',
			sort = {},
			perPage = null,
		) =>
		(dispatch, getState) => {
			let params = buildQueryParams({
				status,
				page,
				perPage,
				filter: searchString,
				sortString: sortToString(sort),
			});
			const url = buildPropertyDatasetListUrl(
				propertyId,
				dataConfig,
				dataSource,
				params,
			);

			dispatch(apiRequest(API_REQUEST, propertyId, status, searchString, sort));

			return get({
				dispatch,
				url,
				onSuccess: (results) => {
					dispatch(apiSuccess(API_SUCCESS, results));

					// Update the property-wide sidebar badge only from a response that
					// describes the whole property: open-scoped, unfiltered, first page.
					const shouldUpdateCount = isPropertyWideCount(
						status,
						searchString,
						page,
					);
					if (
						shouldUpdateCount &&
						results.num_open !== null &&
						results.num_open !== undefined
					) {
						const countField = dataConfig.openIssueCountFields?.[dataSource];
						if (countField) {
							dispatch(updatePropertyCount(countField, results.num_open));
						}
					}
				},
				onError: (error) => dispatch(apiError(API_ERROR, error)),
			});
		};

	/*
	 * Fetch every page and dispatch once with all rows concatenated.
	 *
	 * The grouped work-application table groups rows by job number, and a group is open
	 * or resolved according to its I1 and last S filings. One capped page therefore
	 * cannot render the groups the server counted: a property with 402 filings arrived
	 * as 100 rows and produced 2 of its 12 open groups. Asking the server for a
	 * status-filtered page does not help, because the filings that decide a group's
	 * state are exactly the ones a status filter removes.
	 *
	 * Counts still come from the server, which computes them over every row. This only
	 * supplies the rows the table needs in order to display what those counts describe.
	 */
	const fetchAllPages =
		(
			propertyId,
			status = null,
			searchString = '',
			sort = {},
			perPage = null,
			maxPages = 30,
		) =>
		async (dispatch) => {
			const sortString = sortToString(sort);
			const buildUrl = (page) => {
				const params = buildQueryParams({
					status,
					page,
					perPage,
					filter: searchString,
					sortString,
				});
				if (
					dataSource === CITYPAY ||
					dataSource === NYC_GOV ||
					dataSource === COMBINED ||
					dataSource === FDNY_SCRAPER
				) {
					return `${API_URL}/api/property/${propertyId}/${dataConfig.urlPath}?${params}`;
				}
				return `${API_URL}/api/property/${propertyId}/${getDataSourceApiUrlPath(
					dataSource,
				)}/${dataConfig.urlPath}?${params}`;
			};

			const requestPage = (page) =>
				get({
					dispatch,
					url: buildUrl(page),
					onSuccess: (results) => results,
					onError: (error) => {
						dispatch(apiError(API_ERROR, error));
						return null;
					},
				});

			dispatch(apiRequest(API_REQUEST, propertyId, status, searchString, sort));

			const first = await requestPage(1);
			if (!first) {
				return;
			}

			const rows = [...(first.data || [])];
			const lastPage = Number(first.last_page) || 1;
			// Bounded so a pagination bug cannot spin forever. A truncated set is reported
			// rather than silently rendered as if it were complete.
			const stopAt = Math.min(lastPage, maxPages);

			for (let page = 2; page <= stopAt; page += 1) {
				const next = await requestPage(page);
				if (!next) {
					return;
				}
				rows.push(...(next.data || []));
			}

			if (lastPage > stopAt) {
				console.warn(
					`[${getReducerName(dataConfig, dataSource)}] stopped at page ${stopAt} of ${lastPage}; ` +
						`showing ${rows.length} of ${first.total} rows`,
				);
			}

			dispatch(apiSuccess(API_SUCCESS, { ...first, data: rows }));

			// Every page was fetched, so pagination cannot narrow this response; a
			// search filter still can.
			const shouldUpdateCount = isPropertyWideCount(status, searchString, 1);
			if (
				shouldUpdateCount &&
				first.num_open !== null &&
				first.num_open !== undefined
			) {
				const countField = dataConfig.openIssueCountFields?.[dataSource];
				if (countField) {
					dispatch(updatePropertyCount(countField, first.num_open));
				}
			}
		};

	const silentRefresh = (propertyId) => (dispatch, getState) => {
		let state = getState()[getReducerName(dataConfig, dataSource)];
		let params = buildQueryParams({
			status: state.filteredStatus,
			page: state.currentPage,
			filter: state.searchString,
			sortString: sortToString({
				field: state.sortField,
				direction: state.sortDirection,
			}),
		});

		const url = buildPropertyDatasetListUrl(
			propertyId,
			dataConfig,
			dataSource,
			params,
		);

		return get({
			dispatch,
			url,
			onSuccess: (results) => {
				dispatch(apiSuccess(API_SUCCESS, results));
				const shouldUpdateCount = isPropertyWideCount(
					state.filteredStatus,
					state.searchString,
					state.currentPage,
				);
				if (
					shouldUpdateCount &&
					results.num_open !== null &&
					results.num_open !== undefined
				) {
					const countField = dataConfig.openIssueCountFields?.[dataSource];
					if (countField) {
						dispatch(updatePropertyCount(countField, results.num_open));
					}
				}
			},
			onError: (error) => dispatch(apiError(API_ERROR, error)),
		});
	};

	const reducer = (state = initialState, action) => {
		switch (action.type) {
			case API_REQUEST:
			case API_SUCCESS:
			case API_ERROR:
				return {
					...state,
					...action.payload,
				};
			case `${getReducerName(dataConfig, dataSource)}_STATUSES_SUCCESS`:
			case `${getReducerName(dataConfig, dataSource)}_STATUSES_ERROR`:
				return {
					...state,
					statuses: action.payload.statuses,
				};
			default:
				return state;
		}
	};

	return {
		reducer,
		fetchData,
		fetchAllPages,
		setPage,
		silentRefresh,
	};
}
